import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { IDialogContentComponent } from 'src/app/shared/components/modal-dialog/modal-dialog.service';
import { AppStateService } from 'src/app/shared/services/app-state.service';
import { ReferingVetService } from 'src/app/shared/services/refering-vet.service';
import type { ReferingVetInterface } from 'src/app/shared/models/refering-vet.interface';
import { EntityItemInterface } from 'src/app/shared/components/search-input/select-item.interface';

@Component({
  selector: 'app-share-by-refering-vet',
  templateUrl: './share-by-refering-vet.component.html',
  styleUrls: ['./share-by-refering-vet.component.scss']
})
export class ShareByReferingVetComponent implements OnInit, IDialogContentComponent<ShareByReferingVetPopupResult, undefined> {

  @ViewChild('vetListCard', { read: ElementRef }) vetListCard: ElementRef;

  selectedReferingVets: ReferingVetInterface[] = [];
  onFilterReferingVets: (args: string) => Observable<EntityItemInterface[]>;
  input: undefined;

  private allReferingVets: ReferingVetInterface[] = [];

  constructor(
    private readonly referingVetService: ReferingVetService,
    private readonly appStateService: AppStateService
  ) { }

  getResult(): ShareByReferingVetPopupResult {
    return {
      referingVetIds: this.selectedReferingVets.map(rv => rv.id)
    };
  }

  isDisabled(): boolean {
    return this.selectedReferingVets.length === 0;
  }

  ngOnInit(): void {
    // Load all referring vets once
    if (this.appStateService.clinicId) {
      this.referingVetService.getReferingVets(this.appStateService.clinicId).subscribe(vets => {
        this.allReferingVets = vets;
      });
    }

    // Create filter function for autocomplete
    this.onFilterReferingVets = (searchText: string) => {
      const search = searchText.toLowerCase().trim();

      const filtered = this.allReferingVets.filter(vet => {
        const fullName = `${vet.firstName} ${vet.lastName}`.toLowerCase();
        const email = vet.email.toLowerCase();
        const clinicName = vet.clinicName?.toLowerCase() || '';
        const postalCode = vet.postalCode?.toLowerCase() || '';
        const city = vet.city?.toLowerCase() || '';

        return fullName.includes(search) ||
               email.includes(search) ||
               clinicName.includes(search) ||
               postalCode.includes(search) ||
               city.includes(search);
      });

      return new Observable<EntityItemInterface[]>(observer => {
        const items = filtered.map(vet => ({
          id: vet.id,
          label: this.formatVetLabel(vet)
        }));
        observer.next(items);
        observer.complete();
      });
    };
  }

  onSelectedVetChanged(item: EntityItemInterface) {
    // Find the full ReferingVet object from the selected item ID
    const vet = this.allReferingVets.find(rv => rv.id === item.id);
    if (vet) {
      const vetFound = this.selectedReferingVets.find(rv => rv.id === vet.id);
      if (!vetFound) {
        this.selectedReferingVets.push(vet);
        // Scroll to bottom after adding vet
        setTimeout(() => {
          this.scrollToBottom();
        }, 100);
      }
    }
  }

  private scrollToBottom(): void {
    if (this.vetListCard) {
      const element = this.vetListCard.nativeElement;
      element.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }

  deleteReferingVetById(id: number) {
    this.selectedReferingVets = this.selectedReferingVets.filter(rv => rv.id !== id);
  }

  private formatVetLabel(vet: ReferingVetInterface): string {
    let label = `${vet.lastName} ${vet.firstName} - ${vet.email}`;
    if (vet.clinicName) {
      label += ` (${vet.clinicName})`;
    }

    return label;
  }
}

export interface ShareByReferingVetPopupResult {
  referingVetIds: number[];
}
